import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ApiError } from '../types/api.types';
import { getApiBaseUrl } from '../utils/apiConfig';

export const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for JWT token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      // Don't redirect if we're already on login page
      if (!window.location.pathname.includes('/login')) {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
      }
    }

    // No response = network error (backend unreachable, CORS, etc.)
    if (!error.response) {
      error.message =
        error.code === 'ECONNREFUSED'
          ? 'Server unreachable. Make sure the backend is running (e.g. on port 5000).'
          : error.message || 'Network error. Check your connection and that the API server is running.';
    }

    // Extract error message from response
    const errorData = error.response?.data;
    if (errorData) {
      // Handle validation errors (400 Bad Request)
      if (error.response?.status === 400 && errorData.errors) {
        // Format validation errors for display
        const validationErrors = Object.entries(errorData.errors)
          .map(([field, messages]) => {
            const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
            const messageList = Array.isArray(messages) ? messages.join(', ') : messages;
            return `${fieldName}: ${messageList}`;
          })
          .join('\n');
        error.message = validationErrors || errorData.detail || errorData.message || 'Validation failed';
        // Store errors object for field-specific error handling
        (error as any).fieldErrors = errorData.errors;
      } else {
        // Use detail, message, or default (covers 401, 500, etc.)
        error.message =
          errorData.detail || errorData.message || error.message || 'An error occurred';
      }
    }

    return Promise.reject(error);
  }
);

